/**
 * useCommunications Hook
 * 
 * Provides communication queries and mutations for customer communication history.
 * Uses CRM service queries from the backend.
 * 
 * Requirements: 10.1 - Communication queries
 */

import { useCallback } from 'react';
import { useToast } from '@bloomcode/ui';
import {
  useListMyCommunicationsQuery,
  useGetCommunicationQuery,
  useCountUnreadCommunicationsQuery,
  useMarkCommunicationAsReadMutation,
  useMarkAllCommunicationsAsReadMutation,
  type ListMyCommunicationsQuery,
  type GetCommunicationQuery,
  type CommunicationFiltersInput,
  type PaginationInput,
  type CommunicationType,
} from '@bloomcode/graphql';

// Type aliases for query results
type CommunicationItem = ListMyCommunicationsQuery['listMyCommunications'][number];
type CommunicationDetail = NonNullable<GetCommunicationQuery['getCommunication']>;

// Hook options
interface UseCommunicationsOptions {
  type?: CommunicationType;
  isRead?: boolean;
  limit?: number;
  after?: string;
  startDate?: string;
  endDate?: string;
  skip?: boolean;
}

interface UseCommunicationsResult {
  communications: CommunicationItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  fetchMore: (after: string) => void;
}

/**
 * Hook to fetch customer communications list
 */
export function useCommunications(options: UseCommunicationsOptions = {}): UseCommunicationsResult {
  const { 
    type, 
    isRead,
    limit = 50, 
    after,
    startDate, 
    endDate, 
    skip = false 
  } = options;

  // Build filters
  const filters: CommunicationFiltersInput = {};
  if (type) filters.type = [type];
  if (isRead !== undefined) filters.isRead = isRead;
  if (startDate) filters.startDate = startDate;
  if (endDate) filters.endDate = endDate;

  // Build pagination
  const pagination: PaginationInput = { first: limit };
  if (after) pagination.after = after;

  const { data, loading, error, refetch, fetchMore: apolloFetchMore } = useListMyCommunicationsQuery({
    variables: { filters, pagination },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const fetchMore = useCallback((cursor: string) => {
    apolloFetchMore({
      variables: {
        filters,
        pagination: { first: limit, after: cursor },
      },
    });
  }, [apolloFetchMore, filters, limit]);

  return {
    communications: data?.listMyCommunications ?? [],
    isLoading: loading,
    error: error ?? null,
    refetch,
    fetchMore,
  };
}

interface UseCommunicationDetailOptions {
  communicationId: string;
  skip?: boolean;
}

interface UseCommunicationDetailResult {
  communication: CommunicationDetail | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch a single communication with full details
 */
export function useCommunicationDetail(options: UseCommunicationDetailOptions): UseCommunicationDetailResult {
  const { communicationId, skip = false } = options;

  const { data, loading, error, refetch } = useGetCommunicationQuery({
    variables: { id: communicationId },
    skip: skip || !communicationId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    communication: data?.getCommunication ?? null,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

interface UseCommunicationMutationsResult {
  markAsRead: (communicationId: string) => Promise<void>;
  markAllAsRead: () => Promise<number>;
  isMarkingRead: boolean;
  isMarkingAllRead: boolean;
}

/**
 * Hook for communication mutations (mark as read)
 */
export function useCommunicationMutations(): UseCommunicationMutationsResult {
  const { toast } = useToast();

  const [markAsReadMutation, { loading: isMarkingRead }] = useMarkCommunicationAsReadMutation();
  const [markAllAsReadMutation, { loading: isMarkingAllRead }] = useMarkAllCommunicationsAsReadMutation();

  /**
   * Mark a single communication as read
   */
  const markAsRead = useCallback(async (communicationId: string): Promise<void> => {
    try {
      await markAsReadMutation({
        variables: { id: communicationId },
        refetchQueries: ['ListMyCommunications', 'CountUnreadCommunications'],
      });
    } catch (err) {
      console.error('Failed to mark communication as read:', err);
      toast({
        title: 'Error',
        description: 'Failed to mark communication as read.',
        variant: 'destructive',
      });
      throw err;
    }
  }, [markAsReadMutation, toast]);

  /**
   * Mark all communications as read
   */
  const markAllAsRead = useCallback(async (): Promise<number> => {
    try {
      const result = await markAllAsReadMutation({
        refetchQueries: ['ListMyCommunications', 'CountUnreadCommunications'],
      });

      const count = result.data?.markAllCommunicationsAsRead ?? 0;
      
      if (count > 0) {
        toast({
          title: 'All marked as read',
          description: `${count} communication${count > 1 ? 's' : ''} marked as read.`,
        });
      }

      return count;
    } catch (err) {
      console.error('Failed to mark all communications as read:', err);
      toast({
        title: 'Error',
        description: 'Failed to mark all communications as read.',
        variant: 'destructive',
      });
      throw err;
    }
  }, [markAllAsReadMutation, toast]);

  return {
    markAsRead,
    markAllAsRead,
    isMarkingRead,
    isMarkingAllRead,
  };
}

/**
 * Combined hook for communication management (convenience hook)
 */
export function useCommunicationManagement(communicationId?: string) {
  const communicationsQuery = useCommunications();
  const communicationDetailQuery = useCommunicationDetail({ 
    communicationId: communicationId ?? '', 
    skip: !communicationId 
  });
  const mutations = useCommunicationMutations();

  // Unread count
  const { data: unreadData, refetch: refetchUnread } = useCountUnreadCommunicationsQuery({
    fetchPolicy: 'cache-and-network',
  });

  return {
    // List
    communications: communicationsQuery.communications,
    communicationsLoading: communicationsQuery.isLoading,
    communicationsError: communicationsQuery.error,
    refetchCommunications: communicationsQuery.refetch,
    fetchMoreCommunications: communicationsQuery.fetchMore,
    
    // Detail
    communication: communicationDetailQuery.communication,
    communicationLoading: communicationDetailQuery.isLoading,
    communicationError: communicationDetailQuery.error,
    refetchCommunication: communicationDetailQuery.refetch,
    
    // Unread count
    unreadCount: unreadData?.countUnreadCommunications ?? 0,
    refetchUnreadCount: refetchUnread,
    
    // Mutations
    ...mutations,
  };
}

export default useCommunications;
